import { ProConfigProvider } from '@ant-design/pro-components';
import { useTheme } from '@zhiyue/core';
import { App, ConfigProvider, type ThemeConfig, theme } from 'antd';
import React from 'react';

export const getContentTheme = (dark: boolean): ThemeConfig => ({
  inherit: false,
  algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
  cssVar: { key: dark ? 'cms-dark' : 'cms-light' },
  token: {
    colorPrimary: '#F59E0B',
    colorLink: dark ? '#FBBF24' : '#92400E',
    colorLinkHover: dark ? '#FCD34D' : '#78350F',
    colorLinkActive: dark ? '#F59E0B' : '#78350F',
    colorBgLayout: dark ? '#121212' : '#FFFFFF',
    colorBgContainer: dark ? '#1E1E1E' : '#F6F7F9',
    colorBgElevated: dark ? '#242424' : '#FFFFFF',
    colorText: dark ? '#E4E4E7' : '#1F2633',
    colorTextSecondary: dark ? '#A1A1AA' : '#667085',
    borderRadius: 8,
  },
  components: {
    Button: {
      defaultBg: dark ? '#2B2B2B' : '#EEEEEE',
      defaultColor: dark ? '#E4E4E7' : '#1F2633',
      defaultBorderColor: 'transparent',
      defaultHoverBg: dark ? '#363636' : '#E2E2E2',
      defaultHoverColor: dark ? '#FAFAFA' : '#1F2633',
      defaultHoverBorderColor: 'transparent',
      defaultActiveBg: dark ? '#414141' : '#D6D6D6',
      defaultActiveColor: dark ? '#FAFAFA' : '#1F2633',
      defaultActiveBorderColor: 'transparent',
      defaultGhostColor: dark ? '#E4E4E7' : '#1F2633',
      defaultGhostBorderColor: 'transparent',
      primaryColor: '#1F2633',
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
    },
    Table: {
      headerBg: dark ? '#242424' : '#F6F7F9',
      rowHoverBg: dark ? '#2B2B2B' : '#F0F0F3',
      rowSelectedBg: dark ? '#373025' : '#FFF4DC',
      rowSelectedHoverBg: dark ? '#443725' : '#FFEBC2',
    },
    Card: { headerBg: 'transparent', boxShadowTertiary: 'none' },
  },
});

/** Same session theme at the root and inside ProLayout's content provider. */
const PlatformTheme: React.FC<{
  children: React.ReactNode;
  root?: boolean;
}> = ({ children, root }) => {
  const { navTheme } = useTheme();
  const dark = navTheme === 'realDark';
  const config = React.useMemo(() => getContentTheme(dark), [dark]);
  React.useLayoutEffect(() => {
    if (!root) return;
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    ConfigProvider.config({
      holderRender: (node) => (
        <ConfigProvider theme={config} wave={{ disabled: true }}>
          <App>{node}</App>
        </ConfigProvider>
      ),
    });
  }, [config, dark, root]);
  return (
    <ConfigProvider theme={config} wave={{ disabled: true }}>
      <ProConfigProvider dark={dark}>
        <App>{children}</App>
      </ProConfigProvider>
    </ConfigProvider>
  );
};
export default PlatformTheme;
